<template>
  <div
    id="files-box"
    ref="filesBox"
    tabindex="0"
    @keydown.right="moveWithArrows($event, 1)"
    @keydown.left="moveWithArrows($event, -1)"
    @keydown.down.prevent="moveWithArrows($event, 1 * itemsInRow)"
    @keydown.up.prevent="moveWithArrows($event, -1 * itemsInRow)"
    @keydown.esc="deselectAll"
    @keydown.space.prevent="checkItem"
    @keydown.enter="downloadItem">
    <nav
      v-if="items.length"
      class="d-flex align-center">
      <qx-checkbox
        class="check-item"
        :checked="selectedItems.length === items.length"
        @onChange="toggleSelectAll" />
      <span id="sort-field">
        <translate>by</translate>
        <span class="text-capitalize">{{ sortField }}</span>
        <em
          :class="{
            'icon-arrow-up': sortDirection === 1,
            'icon-arrow-down': sortDirection === -1,
          }"
          class="qx-icon sort-arrow"
          @click.stop="toggleSorting" />
        <qx-popover
          target="#sort-field"
          custom-class="sort-popover">
          <div class="sort-popover-container">
            <p
              v-translate
              class="sort-title">Sort by</p>
            <div class="item-box">
              <input
                id="sort-by-name"
                v-model="sortField"
                type="radio"
                value="name">
              <label
                v-translate
                for="sort-by-name">Name</label>
            </div>
            <div class="item-box">
              <input
                id="sort-by-size"
                v-model="sortField"
                type="radio"
                value="size">
              <label
                v-translate
                for="sort-by-size">Size</label>
            </div>
          </div>
        </qx-popover>
      </span>
      <span
        class="toggle-view"
        :title="listButtonTitle"
        @click="toggleView">
        <em class="qx-icon icon-list" />
      </span>
    </nav>
    <div
      v-if="splitedItems.folders.length"
      class="caption">
      <translate>Folders</translate>
    </div>
    <div
      v-if="splitedItems.folders.length"
      id="folders">
      <folder-grid-item
        v-for="folder in splitedItems.folders"
        ref="item"
        :key="folder.id"
        :folder="folder"
        @click="(meta) => handleClick(folder, meta)"
        @changeCheckedState="changeCheckedState" />
    </div>
    <div
      v-if="splitedItems.files.length"
      class="caption">
      <translate>Files</translate>
    </div>
    <div
      v-if="splitedItems.files.length"
      id="files">
      <file-grid-item
        v-for="file in splitedItems.files"
        :key="file.id"
        ref="item"
        :file="file"
        :is-encrypted="isEncrypted"
        @click="(meta) => handleClick(file, meta)"
        @changeCheckedState="changeCheckedState" />
    </div>
  </div>
</template>

<script>
import FileGridItem from './FileGridItem';
import FolderGridItem from './FolderGridItem';
import QxCheckbox from '@/elements/form/QxCheckbox.vue';
import QxPopover from '@/elements/QxPopover.vue';

import partition from 'lodash/partition';

export default {
  name: 'FilesIconView',
  components: {
    FileGridItem,
    FolderGridItem,
    QxCheckbox,
    QxPopover,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    isEncrypted: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change', 'toggle', 'download'],
  data() {
    return {
      itemsInRow: null,
      sortDirection: 1,
      listButtonTitle: this.$gettext('Show files as list'),
      sortField: 'name',
    };
  },
  computed: {
    splitedItems() {
      const [folders, files] = partition(this.sortedItems, (file) => file.isDir);
      return { folders, files };
    },
    activeItem() {
      return this.sortedItems.findIndex((item) => item.isActiveState);
    },
    selectedItems() {
      return this.sortedItems.filter((file) => file.isCheckedState);
    },
    sortedItems() {
      return this.items.slice(0).sort((a, b) => {
        if (a.isDir && !b.isDir) {
          return -1;
        }
        if (!a.isDir && b.isDir) {
          return 1;
        }
        let sortValue1 = a[this.sortField];
        let sortValue2 = b[this.sortField];

        if (typeof sortValue1 === 'string' && typeof sortValue2 === 'string') {
          const tmpValue1 = sortValue1;
          const tmpValue2 = sortValue2;
          sortValue1 = sortValue1.toLowerCase();
          sortValue2 = sortValue2.toLowerCase();

          if (sortValue1 === sortValue2) {
            sortValue1 = tmpValue1;
            sortValue2 = tmpValue2;
          }
        }
        return sortValue1 > sortValue2 ? this.sortDirection : this.sortDirection * -1;
      });
    },
  },
  mounted() {
    this.countItemsInRow();
  },
  methods: {
    countItemsInRow() {
      const itemWidth = this.$refs.item?.[0].$el.clientWidth;
      this.itemsInRow = Math.floor(this.$refs.filesBox?.clientWidth / itemWidth);
    },
    clickWithShift(index) {
      const lastIndex = this.activeItem;
      const newIndex = index;

      const from = Math.max(0, lastIndex < newIndex ? lastIndex : newIndex);
      const to = Math.min(lastIndex < newIndex ? newIndex : lastIndex, this.sortedItems.length - 1);
      for (let i = from; i <= to; i++) {
        this.sortedItems[i].isCheckedState = true;
        if (i === newIndex) {
          //Set last item as active
          this.activateIndex(newIndex);
        }
      }
      this.triggerChange();
    },
    handleClick(item, { isCtrl, isShift }) {
      const index = this.sortedItems.indexOf(item);

      if (isShift) {
        this.clickWithShift(index);
        return;
      }

      if (isCtrl) {
        //If either item is selected and one is in activeState we need to select it also
        if (this.activeItem !== -1 && !this.sortedItems[this.activeItem].isCheckedState && !this.selectedItems.length) {
          this.selectIndex(this.activeItem);
        }
        this.selectIndex(index);
        this.activateIndex(index);
        return;
      }

      this.activateIndex(index);
      //Clear selection if last is not selected
      if (!this.sortedItems[index].isCheckedState) {
        this.deselectAll();
      }
    },
    scrollToActiveItem() {
      if (this.activeItem !== -1) {
        this.$refs.item[this.activeItem].$el.scrollIntoView({ block: 'nearest' });
      }
    },
    selectIndex(index) {
      this.sortedItems[index].isCheckedState = !this.sortedItems[index].isCheckedState;
      this.triggerChange();
    },
    activateIndex(index) {
      this.sortedItems.forEach((item) => {
        item.isActiveState = false;
      });

      this.sortedItems[index].isActiveState = true;
      this.scrollToActiveItem();
    },
    deselectAll() {
      this.sortedItems.forEach((model) => {
        model.isCheckedState = false;
      });
      this.triggerChange();
    },
    toggleSelectAll() {
      const isAllSelected = this.selectedItems.length === this.sortedItems.length;
      this.sortedItems.forEach((model) => {
        model.isCheckedState = !isAllSelected;
      });
      this.triggerChange();
    },
    moveWithArrows({ shiftKey: isShift }, direction) {
      //Either file is in activeState
      if (this.activeItem === -1) {
        this.sortedItems[0].isActiveState = true;
        return;
      }

      let newIndex = this.activeItem + direction;

      this.countItemsInRow();

      if (isShift) {
        this.clickWithShift(newIndex);
        return;
      }

      //Prevent moves at end forward and at the beggin before
      if (newIndex < 0) {
        if (this.activeItem === 0) {
          return;
        }
        newIndex = 0;
      }
      if (newIndex >= this.sortedItems.length) {
        if (this.activeItem === this.sortedItems.length - 1) {
          return;
        }
        newIndex = this.sortedItems.length - 1;
      }

      //When move with up or down key
      if (this.itemsInRow === Math.abs(direction)) {
        // When move from folders to files with down key move to last folder instead file
        if (this.sortedItems[this.activeItem].isDir && !this.sortedItems[newIndex].isDir) {
          //When last folder move to first file
          if (this.activeItem + 1 === this.splitedItems.folders.length) {
            this.activateIndex(this.sortedItems.indexOf(this.splitedItems.files[0]));
            return;
          }
          this.activateIndex(this.sortedItems.indexOf(this.splitedItems.folders[this.splitedItems.folders.length - 1]));
          return;
        }
        // When move from files to folders with up key move to last folder instead file

        if (!this.sortedItems[this.activeItem].isDir && this.sortedItems[newIndex].isDir) {
          // If last folders row is not filled move to last folder
          if (this.splitedItems.folders.length % this.itemsInRow !== 0) {
            this.activateIndex(
              this.sortedItems.indexOf(this.splitedItems.folders[this.splitedItems.folders.length - 1])
            );
            return;
          }
        }
      }

      this.activateIndex(newIndex);
    },
    changeCheckedState(item) {
      item.isCheckedState = !item.isCheckedState;
      this.triggerChange();
    },
    triggerChange() {
      this.$emit(
        'change',
        this.selectedItems.map((file) => file.id)
      );
    },
    checkItem() {
      if (this.activeItem !== -1) {
        this.selectIndex(this.activeItem);
      }
    },
    toggleSorting() {
      this.sortDirection *= -1;
    },
    toggleView() {
      this.$emit('toggle');
    },
    downloadItem() {
      if (this.activeItem === -1 || !this.activeItem.isDownloadAllowed) {
        return;
      }
      if (this.isEncrypted) {
        this.$refs.item[this.activeItem]?.$refs?.downloadButton?.$refs?.decryptButton.downloadAction();
      } else {
        this.$emit('download', [this.sortedItems[this.activeItem].id]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#files-box {
  nav {
    width: 99%;
    padding-bottom: 5px;
    border-bottom: 1px solid $gray-200;
    cursor: pointer;
    #sort-field {
      margin-left: 10px;
      color: $base-grey;
      font-family: $regular-sans-font;
      em {
        color: $gray-600;
      }
    }
    .toggle-view {
      margin-left: auto;
      color: $base-grey;
    }
  }

  .sort-arrow {
    font-size: 14px;
  }

  #folders,
  #files {
    display: flex;
    flex-wrap: wrap;
  }

  #folders {
    margin-bottom: 30px;
  }

  :deep .download-box {
    position: relative;
    cursor: pointer;
    width: 24%;
    margin-bottom: 10px;
    color: gray;
    margin-right: 1%;
    border-radius: 4px;
    border: 1px solid $gray-400;
    @media (max-width: 1366px) {
      width: 32%;
    }
    @media (max-width: 766px) {
      width: 48%;
    }
    &.active-file,
    &.checked {
      border: 1px solid $base-blue;
      .check-item {
        visibility: visible;
      }
    }

    .file-meta {
      user-select: none;
      .file-name {
        @extend %ellipsisOverflow;
        color: $black;
        font-family: $regular-font;
        line-height: 24px;
        font-size: 15px;
      }
      .file-size {
        font-size: 14px;
      }
    }
    .check-item {
      margin-left: auto;
      visibility: hidden;
    }
    &:hover {
      .check-item {
        visibility: visible;
      }
    }
  }
  .caption {
    user-select: none;
    margin: 12px 0px;
    color: $gray-900;
    font-family: $regular-font;
    font-size: 15px;
  }
}
</style>
<style lang="scss">
.download-box {
  .icon-download {
    color: $white !important;
    padding: 6px;
    background: $base-blue;
    border-radius: 3px;
    font-size: 20px;
  }
}

.sort-popover {
  .popover-body {
    padding: 0 10 0 0;
  }
  .sort-popover-container {
    padding: 0 8px;
    .sort-title {
      color: $grey;
      text-transform: uppercase;
      font-size: 11.5px;
      padding-top: 3px;
      padding-bottom: 5px;
      margin: 0;
    }
    .item-box {
      @extend %dspFlex;
      @include align-items(center);
      line-height: 27px;
      margin-bottom: 5px;
      label {
        margin: 0 0 0 6px;
      }
    }
  }
}
</style>
